import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { Modal, Button } from 'react-bootstrap'

interface ConfirmOptions {
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    variant?: 'danger' | 'primary' | 'warning'
    onConfirm: () => void | Promise<void>
    onCancel?: () => void
}

interface ConfirmContextType {
    confirm: (options: ConfirmOptions) => void
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined)

export const useConfirm = () => {
    const context = useContext(ConfirmContext)
    if (!context) {
        throw new Error('useConfirm must be used within ConfirmProvider')
    }
    return context
}

interface ConfirmProviderProps {
    children: ReactNode
}

export const ConfirmProvider: React.FC<ConfirmProviderProps> = ({ children }) => {
    const [options, setOptions] = useState<ConfirmOptions | null>(null)
    const [loading, setLoading] = useState(false)

    const confirm = useCallback((opts: ConfirmOptions) => {
        setOptions(opts)
    }, [])

    const handleConfirm = async () => {
        if (!options) return

        setLoading(true)
        try {
            await options.onConfirm()
            setOptions(null)
        } catch (error) {
            console.error('Confirm action failed:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        if (options?.onCancel) {
            options.onCancel()
        }
        setOptions(null)
    }

    return (
        <ConfirmContext.Provider value={{ confirm }}>
            {children}

            <Modal show={!!options} onHide={handleCancel} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{options?.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{options?.message}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={loading}
                    >
                        {options?.cancelText || 'Cancel'}
                    </Button>
                    <Button
                        variant={options?.variant || 'primary'}
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Loading...
                            </>
                        ) : (
                            options?.confirmText || 'Confirm'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </ConfirmContext.Provider>
    )
}
